import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Search, Shuffle } from 'lucide-react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { useCoupons, addAdminCoupon, setCouponStatus } from '../../api/catalog';

type CouponStatusFilter = 'all' | 'active' | 'draft' | 'expired' | 'disabled';

/** Coupons — table + new-coupon drawer (persists via /admin/coupons). */
export function Coupons() {
  const { data: coupons = [], isLoading } = useCoupons();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CouponStatusFilter>('all');
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const filtered = coupons.filter((c) => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    return !search || c.code.toLowerCase().includes(search.toLowerCase());
  });

  async function toggleStatus(c: { id: string; status: string }) {
    setTogglingId(c.id);
    try {
      await setCouponStatus(c.id, c.status === 'active' ? 'disabled' : 'active');
      await queryClient.invalidateQueries({ queryKey: ['coupons'] });
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title="Coupons"
        subtitle="Promo codes for percent / fixed / trial-extension / free-intervals."
        action={<Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>New coupon</Button>}
      />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <StatusFilter value={statusFilter} onChange={setStatusFilter} />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search code…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Code</th>
                <th className="text-left font-medium px-4 py-3">Type</th>
                <th className="text-left font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3">Redeemed</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Expires</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 7 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <Skeleton className="h-4 w-24 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-32 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-12 rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {coupons.length === 0 ? 'No coupons yet.' : 'No coupons match your filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <div className="font-medium text-zinc-950 dark:text-zinc-50 mono">{c.code}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 capitalize">{c.discountType.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{c.amountLabel}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">
                    {c.redemptionsUsed} / {c.redemptionsLimit ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums">
                    {c.expiresAt ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={c.status === 'active' ? 'success' : c.status === 'expired' ? 'warning' : 'neutral'}>
                      {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    {(c.status === 'active' || c.status === 'disabled' || c.status === 'draft') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={togglingId === c.id}
                        onClick={() => void toggleStatus(c)}
                      >
                        {c.status === 'active' ? 'Disable' : 'Activate'}
                      </Button>
                    )}
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <NewCouponDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['coupons'] });
          setDrawerOpen(false);
        }}
      />
    </div>
  );
}

function StatusFilter({ value, onChange }: { value: CouponStatusFilter; onChange: (v: CouponStatusFilter) => void }) {
  const [open, setOpen] = useState(false);
  const options: Array<{ v: CouponStatusFilter; l: string }> = [
    { v: 'all', l: 'All' },
    { v: 'active', l: 'Active' },
    { v: 'draft', l: 'Draft' },
    { v: 'expired', l: 'Expired' },
    { v: 'disabled', l: 'Disabled' },
  ];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">Status:</span>
        <span>{options.find((o) => o.v === value)?.l}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[160px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => { onChange(o.v); setOpen(false); }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NewCouponDrawer({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const [type, setType] = useState<'percent' | 'fixed' | 'trial_extension' | 'free_intervals'>('percent');
  const [code, setCode] = useState('');
  const [value, setValue] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const valueNum = Number(value);
  const codeValid = /^[A-Z0-9][A-Z0-9_-]{1,31}$/.test(code.trim().toUpperCase());
  const valueValid = Number.isFinite(valueNum) && valueNum > 0 && (type !== 'percent' || valueNum <= 100);
  const valid = codeValid && valueValid;

  const valueLabel =
    type === 'percent' ? 'Percent off' : type === 'fixed' ? 'Amount off (BDT)' : type === 'trial_extension' ? 'Extra trial days' : 'Free intervals';

  async function handleSave() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      await addAdminCoupon({
        code: code.trim().toUpperCase(),
        discountType: type,
        // fixed is entered in BDT → minor units; others are plain counts/percent.
        discountValue: type === 'fixed' ? Math.round(valueNum * 100) : Math.round(valueNum),
        maxRedemptions: maxRedemptions.trim() === '' ? null : Number(maxRedemptions),
        expiresAt: expiresAt || null,
      });
      onSaved();
      setCode('');
      setValue('');
      setMaxRedemptions('');
      setExpiresAt('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the coupon. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New coupon"
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSave} loading={submitting} disabled={!valid}>Create coupon</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
          <Input
            label="Code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            error={code && !codeValid ? '2–32 chars: A–Z, 0–9, - and _' : undefined}
            className="mono"
          />
          <Button variant="secondary" iconLeft={<Shuffle className="w-3.5 h-3.5" />} onClick={() => setCode('PROMO-' + Math.random().toString(36).slice(2, 7).toUpperCase())}>
            Random
          </Button>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Discount type</span>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'percent', l: 'Percent off' },
              { v: 'fixed', l: 'Fixed amount off' },
              { v: 'trial_extension', l: 'Trial extension' },
              { v: 'free_intervals', l: 'Free first N intervals' },
            ].map((t) => (
              <label key={t.v} className={cn(
                'flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md border transition',
                type === t.v
                  ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                  : 'border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800',
              )}>
                <input type="radio" name="type" checked={type === t.v} onChange={() => setType(t.v as typeof type)} className="text-brand focus:ring-brand" />
                {t.l}
              </label>
            ))}
          </div>
        </div>

        <Input
          label={valueLabel}
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          suffix={<span className="text-xs">{type === 'percent' ? '%' : type === 'fixed' ? '৳' : type === 'trial_extension' ? 'days' : 'intervals'}</span>}
          error={value && !valueValid ? (type === 'percent' ? 'Must be 1–100' : 'Must be a positive number') : undefined}
        />

        <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-zinc-200 dark:border-zinc-700">
          <Input
            label="Max redemptions"
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            placeholder="∞"
            hint="Total across all customers — blank for unlimited"
          />
          <Input label="Expires" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} hint="Blank = never expires" />
        </div>

        <Banner tone="info">
          The coupon is created <strong>active</strong>. Checkout-time validation (redemption caps, expiry, package
          scoping) is enforced by the billing engine when the code is applied.
        </Banner>

        {saveError && (
          <Banner tone="error" title="Couldn’t save the coupon">
            {saveError}
          </Banner>
        )}
      </div>
    </Drawer>
  );
}
